import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Package, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-sm border border-border bg-card">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="font-heading font-semibold flex items-center gap-2"><Icon size={17} className="text-primary" /> {title}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  );
}

function Row({ children, onDelete, testid }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-border last:border-0" data-testid={testid}>
      <div className="flex-1 min-w-0">{children}</div>
      <button onClick={onDelete} className="text-muted-foreground hover:text-primary transition-colors"><Trash2 size={15} /></button>
    </div>
  );
}

export default function Settings() {
  const [plans, setPlans] = useState([]);
  const [antivirus, setAntivirus] = useState([]);
  const [plan, setPlan] = useState({ name: "", price: "", years: "1" });
  const [av, setAv] = useState("");

  const load = () => {
    api.get("/catalog/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/catalog/antivirus").then((r) => setAntivirus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addPlan = async () => {
    if (!plan.name.trim()) { toast.error("Plan name required"); return; }
    const years = Math.min(3, Math.max(1, parseInt(plan.years) || 1));
    await api.post("/catalog/plans", { name: plan.name, price: parseFloat(plan.price) || 0, duration_days: years * 365 });
    setPlan({ name: "", price: "", years: "1" }); load(); toast.success("Plan added");
  };
  const addAv = async () => { if (!av.trim()) return; await api.post("/catalog/antivirus", { name: av }); setAv(""); load(); toast.success("Antivirus added"); };
  const del = async (kind, id) => { await api.delete(`/catalog/${kind}/${id}`); load(); };

  const yearsOf = (days) => Math.max(1, Math.round((days || 365) / 365));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage the dropdowns used on the New Customer form.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Section icon={Package} title="Plans" desc="Sale plans with price & subscription length (in years)">
          <div className="grid gap-2">
            <Input placeholder="Plan name" value={plan.name} onChange={(e) => setPlan({ ...plan, name: e.target.value })} className="rounded-sm" data-testid="plan-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Price" type="number" value={plan.price} onChange={(e) => setPlan({ ...plan, price: e.target.value })} className="rounded-sm font-mono" data-testid="plan-price" />
              <Select value={plan.years} onValueChange={(v) => setPlan({ ...plan, years: v })}>
                <SelectTrigger className="rounded-sm" data-testid="plan-years"><SelectValue placeholder="Years" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 year</SelectItem>
                  <SelectItem value="2">2 years</SelectItem>
                  <SelectItem value="3">3 years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="rounded-sm gap-1.5" onClick={addPlan} data-testid="add-plan-btn"><Plus size={15} /> Add plan</Button>
          </div>
          <div>
            {plans.map((p) => (
              <Row key={p.id} onDelete={() => del("plans", p.id)} testid={`plan-${p.id}`}>
                <p className="text-sm font-medium">{p.name}</p>
                <p className="text-xs text-muted-foreground font-mono">${p.price} · {yearsOf(p.duration_days)} year{yearsOf(p.duration_days) !== 1 && "s"}</p>
              </Row>
            ))}
          </div>
        </Section>

        <Section icon={ShieldCheck} title="Antivirus Products" desc="Antivirus options for a sale">
          <div className="flex gap-2">
            <Input placeholder="Antivirus name" value={av} onChange={(e) => setAv(e.target.value)} className="rounded-sm" data-testid="av-name" />
            <Button className="rounded-sm" onClick={addAv} data-testid="add-av-btn"><Plus size={15} /></Button>
          </div>
          <div>
            {antivirus.map((a) => (
              <Row key={a.id} onDelete={() => del("antivirus", a.id)} testid={`av-${a.id}`}><p className="text-sm font-medium">{a.name}</p></Row>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
